import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Lenis from 'lenis';
import { Music, Sparkles, Calendar, Instagram, Youtube, Menu, X, Monitor, Tv, Zap, Disc3, Film, Eye, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const HERO_IMAGE = "https://customer-assets-lxgj4vgw.emergentagent.net/job_val-collective/artifacts/vafe5jea_openart-image_1790432375926_3a5b4b7a_1790432376364_69b97d9c.webp";
const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_val-collective/artifacts/8d5o61xk_DJ%20VAL%20-%20GOLD%202.png";

const EASE = [0.22, 1, 0.36, 1];

const NAV_ITEMS = [
  { name: 'Bio', id: 'bio' },
  { name: 'Music', id: 'music' },
  { name: 'Gallery', id: 'gallery' },
  { name: 'Booking', id: 'booking' },
];

const MARQUEE_ITEMS = ['FUNK', 'DISCO', 'SOUL', 'HOUSE', 'AFROBEATS', 'R&B', 'HIP-HOP', 'THROWBACKS', 'OPEN FORMAT', 'LUXURY EVENTS'];

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: '110%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const SectionHeading = ({ index, label, title }) => (
  <Reveal className="text-center mb-20">
    <p className="text-xs md:text-sm tracking-[0.5em] text-amber-600 font-semibold uppercase mb-6">{index} — {label}</p>
    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-wide">{title}</h2>
  </Reveal>
);

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lenisRef = useRef(null);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const heroFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.085, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (id) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(`#${id}`, { duration: 1.5 });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-black text-white overflow-hidden">
      {/* Navigation Bar */}
      <nav
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrollY > 100 ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="#hero" data-testid="nav-logo-link" onClick={(e) => { e.preventDefault(); scrollToSection('hero'); }} className="flex items-center">
            <img
              src={LOGO_IMAGE}
              alt="DJ VAL Logo"
              className="h-24 md:h-28 w-auto transition-transform duration-300 hover:scale-105"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-link-${item.id}`}
                onClick={() => scrollToSection(item.id)}
                className="text-white hover:text-amber-600 font-medium text-sm tracking-wider transition-colors duration-300 uppercase"
              >
                {item.name}
              </button>
            ))}
            <Button
              data-testid="nav-book-now-btn"
              onClick={() => scrollToSection('booking')}
              className="bg-amber-600 hover:bg-amber-700 text-black font-semibold px-6 py-2 text-sm rounded-none transition-all duration-300 hover:scale-105"
            >
              BOOK NOW
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white hover:text-amber-600 transition-colors"
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden bg-black/98 backdrop-blur-lg border-t border-neutral-800">
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-link-${item.id}`}
                  onClick={() => {
                    scrollToSection(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className="text-white hover:text-amber-600 font-medium text-lg tracking-wider transition-colors duration-300 uppercase text-left"
                >
                  {item.name}
                </button>
              ))}
              <Button
                data-testid="mobile-book-now-btn"
                onClick={() => {
                  scrollToSection('booking');
                  setMobileMenuOpen(false);
                }}
                className="bg-amber-600 hover:bg-amber-700 text-black font-semibold px-6 py-3 text-sm rounded-none transition-all duration-300 w-full"
              >
                BOOK NOW
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" ref={heroRef} data-testid="hero-section" className="relative h-screen overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-cover hero-bg"
          style={{
            y: bgY,
            scale: bgScale,
            backgroundImage: `url('${HERO_IMAGE}')`,
            filter: 'grayscale(100%) contrast(1.15)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />

        <motion.div
          style={{ opacity: heroFade }}
          className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-28 md:pb-24"
        >
          <div className="grid md:grid-cols-12 gap-10 items-end w-full">
            {/* Text Content */}
            <div className="md:col-span-12 text-left">
              <MaskedLine delay={0.15}>
                <span className="text-[11px] md:text-sm tracking-[0.45em] text-amber-500 font-semibold uppercase">
                  Velario Dacosta · Open Format DJ
                </span>
              </MaskedLine>

              <h1 className="font-display uppercase mt-4 md:mt-6">
                <MaskedLine delay={0.3}>
                  <span className="block text-[13vw] md:text-[6.2vw] leading-[0.95] text-white">Dubai's Finest</span>
                </MaskedLine>
                <MaskedLine delay={0.45}>
                  <span className="sr-only">Funk &amp; Disco DJ</span>
                  <svg viewBox="0 0 620 100" className="block w-[88vw] md:w-[58vw] max-w-full" aria-hidden="true">
                    <text x="0" y="80" textLength="600" lengthAdjust="spacingAndGlyphs" className="hero-funk-fill">FUNK &amp; DISCO DJ</text>
                    <text x="0" y="80" textLength="600" lengthAdjust="spacingAndGlyphs" className="hero-funk-sparkle">FUNK &amp; DISCO DJ</text>
                  </svg>
                </MaskedLine>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
                className="mt-5 md:mt-7 text-xs md:text-base text-neutral-300 tracking-[0.25em] uppercase"
              >
                Club · Private Events · Luxury Experiences
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                className="mt-4 md:mt-6 text-lg md:text-2xl italic text-neutral-400 font-serif max-w-xl"
              >
                "I don't just play music. I create moments people remember."
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                className="mt-7 md:mt-10 flex flex-col sm:flex-row gap-4"
              >
                <Button
                  data-testid="hero-book-btn"
                  onClick={() => scrollToSection('booking')}
                  className="bg-amber-600 hover:bg-amber-500 text-black font-semibold px-10 md:px-12 py-5 md:py-6 text-base md:text-lg rounded-none transition-all duration-300 hover:scale-105 tracking-wider"
                >
                  BOOK DJ VAL
                </Button>
                <Button
                  data-testid="hero-listen-btn"
                  onClick={() => scrollToSection('music')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-black font-semibold px-10 md:px-12 py-5 md:py-6 text-base md:text-lg rounded-none transition-all duration-300 tracking-wider"
                >
                  LISTEN NOW
                </Button>
              </motion.div>
            </div>

          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.button
          data-testid="scroll-cue-btn"
          onClick={() => scrollToSection('bio')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden lg:flex flex-col items-center gap-3 group cursor-pointer"
          aria-label="Scroll to bio section"
        >
          <span className="text-[10px] tracking-[0.5em] uppercase text-neutral-400 group-hover:text-amber-500 transition-colors duration-300">Scroll</span>
          <span className="scroll-line" />
        </motion.button>
      </section>

      {/* Editorial Marquee */}
      <div data-testid="marquee-section" className="marquee border-y border-amber-600/30 bg-black py-5 select-none" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center shrink-0">
              {MARQUEE_ITEMS.map((item, i) => (
                <span key={i} className="flex items-center">
                  <span className="font-display text-2xl md:text-3xl uppercase tracking-wider text-neutral-200 px-6">{item}</span>
                  <span className="text-amber-600 text-lg">•</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Bio Section */}
      <section id="bio" data-testid="bio-section" className="py-32 px-6 bg-gradient-to-b from-black to-neutral-950">
        <div className="max-w-6xl mx-auto">
          <SectionHeading index="01" label="The Man Behind The Decks" title="The Architect of Atmosphere" />
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <Reveal className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-amber-600/40 pointer-events-none" />
              <img
                src="https://customer-assets.emergentagent.com/job_cfb3653d-9aad-46db-9b20-667ffb19f4f0/artifacts/y7pcj325_ChatGPT%20Image%20Feb%2021%2C%202026%2C%2005_02_19%20PM.png"
                alt="DJ VAL"
                className="relative w-full h-auto hover:scale-[1.02] transition-transform duration-700"
                style={{ filter: 'grayscale(100%) contrast(1.25)' }}
              />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="space-y-6 text-lg text-neutral-300 leading-relaxed">
                <p>
                  Velario Dacosta, better known as <span className="text-amber-600 font-semibold">DJ VAL</span>, is the kind of DJ who reads a room before the first beat drops. With over 25 years behind the decks, he has mastered the art of turning dance floors into unforgettable moments.
                </p>
                <p>
                  His open-format style seamlessly blends hip-hop, throwbacks, commercial hits, and high-energy club anthems into one electrifying experience. From exclusive nightlife venues in Dubai, India, Oman, Kenya, and Qatar to luxury private events, DJ VAL doesn't just play tracks — he curates atmospheres.
                </p>
                <p>
                  The kind that lingers long after the lights come on.
                </p>
                <p className="text-xl italic text-neutral-200 border-l-4 border-amber-600 pl-6 font-serif">
                  His sound is nostalgic, modern, and magnetic — built for crowds that came to feel something.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Music Section */}
      <section id="music" data-testid="music-section" className="py-32 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <SectionHeading index="02" label="Sound Experience" title="Every Set Is A Journey" />
          <Reveal className="text-center -mt-10 mb-20">
            <p className="text-xl text-neutral-400 max-w-3xl mx-auto">
              From 80s &amp; 90s nostalgia to Contemporary Club Energy — <span className="text-amber-600 font-serif text-2xl italic">pressed live, never programmed.</span>
            </p>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: 'Commercial House Anthems',
                url: 'https://www.mixcloud.com/widget/iframe/?hide_cover=0&light=1&feed=%2FDJ__VAL%2Fdj-val-commercial-house-anthems-dance-2024%2F',
              },
              {
                title: 'Afrobeats & Dancehall',
                url: 'https://www.mixcloud.com/widget/iframe/?hide_cover=0&light=1&feed=%2FDJ__VAL%2Fdj-val-afrobeats-dancehall-2024%2F',
              },
              {
                title: 'Retro Rewind: 80s & 90s',
                url: 'https://www.mixcloud.com/widget/iframe/?hide_cover=0&light=1&feed=%2FDJ__VAL%2Fretro-rewind-back-to-the-80s-90s%2F',
              },
            ].map((mix, index) => (
              <Reveal key={index} delay={index * 0.12} className="tv-frame-container">
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
                  <div className="vinyl-record"></div>
                </div>

                <div className="tv-frame" style={{ position: 'relative', zIndex: 1 }}>
                  <div className="tv-screen">
                    <div className="bg-black overflow-hidden">
                      <iframe
                        data-testid={`mixcloud-player-${index}`}
                        width="100%"
                        height="160"
                        src={mix.url}
                        frameBorder="0"
                        allow="autoplay"
                        title={mix.title}
                        className="mixcloud-player"
                      />
                    </div>
                  </div>
                  <div className="tv-stand"></div>
                </div>
                <p className="text-center mt-4 text-neutral-400 text-sm">{mix.title}</p>
              </Reveal>
            ))}
          </div>

          {/* YouTube Videos Section */}
          <div className="mt-20">
            <Reveal>
              <h3 className="font-display text-3xl md:text-4xl uppercase mb-12 text-center tracking-wide">Video Performances</h3>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: 'DJ VAL Live Performance', videoId: 'IUoDzeuntFg' },
                { title: 'Live RNB Ol\'Skool Set', videoId: 'm9r5zCioD4Q' },
                { title: 'DJ VAL Mix Session', videoId: 'Ci6ydLbbQMM' },
              ].map((video, index) => (
                <Reveal key={index} delay={index * 0.12} className="tv-frame-container">
                  <div className="tv-frame">
                    <div className="tv-screen">
                      <div className="aspect-video bg-black overflow-hidden">
                        <iframe
                          data-testid={`youtube-player-${index}`}
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${video.videoId}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={video.title}
                        />
                      </div>
                    </div>
                    <div className="tv-stand"></div>
                  </div>
                  <p className="text-center mt-4 text-neutral-400 text-sm">{video.title}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visual DJ Experience Section */}
      <section id="visual-dj" data-testid="visual-dj-section" className="py-32 px-6 bg-black relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent opacity-50" />

        <div className="max-w-6xl mx-auto">
          <SectionHeading index="03" label="Visual DJ Experience" title="Sound You Can See" />

          <Reveal className="max-w-4xl mx-auto mb-16">
            <p className="text-2xl md:text-3xl text-center mb-8 font-light text-neutral-200">
              DJ VAL doesn't just program sound — <span className="text-amber-600 font-semibold">he curates Moments.</span>
            </p>

            <div className="space-y-6 text-lg text-neutral-300 leading-relaxed">
              <p>
                As a professional <span className="text-white font-semibold">Video DJ</span>, he delivers fully synchronized music videos, curated visual loops, and high-impact screen content designed to elevate venues with LED walls, projection systems, and multi-screen setups.
              </p>
              <p>
                From high-energy club nights to themed throwback experiences, every track can be paired with matching visuals, original music videos, branded content, or curated visual storytelling — transforming the atmosphere into a <span className="text-amber-600">full audiovisual journey.</span>
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-12 mb-16">
            {[
              { icon: <Disc3 className="w-16 h-16" />, title: 'Music Programming', description: 'Expertly curated soundtracks' },
              { icon: <Film className="w-16 h-16" />, title: 'Visual Content', description: 'Synchronized cinematic experiences' },
              { icon: <Eye className="w-16 h-16" />, title: 'Immersive Atmosphere', description: 'Captivating sensory experiences' },
            ].map((item, index) => (
              <Reveal key={index} delay={index * 0.12} className="text-center group">
                <div className="mb-6 flex justify-center text-amber-600 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3 group-hover:text-amber-600 transition-colors">
                  {item.title}
                </h3>
                <p className="text-neutral-400">{item.description}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="bg-gradient-to-br from-neutral-900 to-black border-2 border-amber-600/30 p-8 md:p-12 rounded-none relative overflow-hidden">
            <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-amber-600"></div>
            <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-amber-600"></div>

            <h3 className="font-display text-3xl md:text-5xl uppercase mb-4 text-center tracking-wide">
              Designed for venues that value
              <span className="block text-amber-600 mt-2">atmosphere</span>
            </h3>
            <div className="w-32 h-1 bg-amber-600 mx-auto mb-12"></div>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {[
                { icon: <Tv className="w-6 h-6" />, text: 'Nightclubs with LED walls' },
                { icon: <Monitor className="w-6 h-6" />, text: 'Lounge bars with TV screens' },
                { icon: <Sparkles className="w-6 h-6" />, text: 'Branded venue activations' },
                { icon: <Sparkles className="w-6 h-6" />, text: 'Luxury hospitality spaces' },
                { icon: <Music className="w-6 h-6" />, text: 'Themed event nights' },
                { icon: <Zap className="w-6 h-6" />, text: 'Multi-screen setups' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 text-lg group hover:text-amber-600 transition-colors"
                >
                  <div className="text-amber-600 group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-600 to-transparent opacity-50" />
      </section>

      {/* Gallery Section */}
      <section id="gallery" data-testid="gallery-section" className="py-32 px-6 bg-black">
        <div className="max-w-7xl mx-auto">
          <SectionHeading index="04" label="Gallery" title="Moments In Monochrome" />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'https://customer-assets-lxgj4vgw.emergentagent.net/job_val-collective/artifacts/2zb9xqjo_Image%20Mar%204%2C%202026%20at%2008_25_34%20PM.png',
              'https://customer-assets.emergentagent.com/job_cfb3653d-9aad-46db-9b20-667ffb19f4f0/artifacts/jc35se6d_ChatGPT%20Image%20Feb%2021%2C%202026%2C%2005_37_59%20PM.png',
              'https://customer-assets-lxgj4vgw.emergentagent.net/job_val-collective/artifacts/u4qi63d6_Image%20Mar%204%2C%202026%20at%2008_35_52%20PM.png',
              'https://customer-assets-lxgj4vgw.emergentagent.net/job_val-collective/artifacts/6kww4kxl_%20%20Image%20Feb%2021%2C%202026%2C%2005_22_00%20PM.png',
              'https://customer-assets-lxgj4vgw.emergentagent.net/job_val-collective/artifacts/yan05zwh_%20%20Image%20Feb%2019%2C%202026%2C%2010_41_18%20PM.png',
              'https://customer-assets.emergentagent.com/job_val-collective/artifacts/ada0byrv_ChatGPT%20Image%20Feb%2019%2C%202026%2C%2009_22_21%20PM.png',
              'https://customer-assets.emergentagent.com/job_val-collective/artifacts/d1158f63_ChatGPT%20Image%20Feb%2019%2C%202026%2C%2010_25_31%20PM.png',
            ].map((img, index) => (
              <Reveal key={index} delay={(index % 3) * 0.1} className="group relative overflow-hidden aspect-square cursor-pointer gallery-item">
                <img
                  src={img}
                  alt={`DJ VAL ${index + 1}`}
                  data-testid={`gallery-image-${index}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                  style={{ filter: 'grayscale(100%) contrast(1.25)' }}
                />
                <div className="absolute inset-0 border-4 border-transparent group-hover:border-amber-600 transition-all duration-500" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Book Section */}
      <section id="why-book" data-testid="why-book-section" className="py-32 px-6 bg-gradient-to-b from-black to-neutral-950">
        <div className="max-w-6xl mx-auto">
          <SectionHeading index="05" label="Why Book DJ VAL" title="The Difference Is In The Details" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { icon: <Sparkles className="w-12 h-12" />, title: 'Crowd Energy Expert', description: 'Reads the room and adapts in real-time to keep energy levels soaring.' },
              { icon: <Music className="w-12 h-12" />, title: 'Open-Format Versatility', description: 'Seamlessly blends 70s, 80s, 90s, hip-hop, house, afrobeats, and beyond.' },
              { icon: <Calendar className="w-12 h-12" />, title: 'Professional & Reliable', description: '25+ years of experience with global brands and luxury venues.' },
              { icon: <Sparkles className="w-12 h-12" />, title: 'Luxury Event Experience', description: 'Curated atmospheres for high-end private events and celebrity functions.' },
            ].map((item, index) => (
              <Reveal key={index} delay={index * 0.1} className="text-center group">
                <div className="mb-6 flex justify-center text-amber-600 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-semibold mb-4 group-hover:text-amber-600 transition-colors">
                  {item.title}
                </h3>
                <p className="text-neutral-400 leading-relaxed">{item.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" data-testid="booking-section" className="py-32 px-6 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <SectionHeading index="06" label="Booking" title="Ready To Elevate Your Event?" />
          <Reveal>
            <p className="text-2xl text-neutral-400 mb-16 -mt-10">Let's create something unforgettable.</p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <a
                href="mailto:reachdjval@gmail.com"
                data-testid="booking-email-card"
                className="bg-neutral-900 hover:bg-neutral-800 border-2 border-neutral-800 hover:border-amber-600 p-8 transition-all duration-300 rounded-none group"
              >
                <div className="flex justify-center mb-4">
                  <Mail className="w-12 h-12 text-amber-600 group-hover:scale-110 transition-transform" />
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-amber-600 transition-colors text-center">Email</h3>
                <p className="text-neutral-400 text-center">reachdjval@gmail.com</p>
              </a>

              <a
                href="https://wa.me/971566392312"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-whatsapp-card"
                className="bg-neutral-900 hover:bg-neutral-800 border-2 border-neutral-800 hover:border-amber-600 p-8 transition-all duration-300 rounded-none group"
              >
                <div className="flex justify-center mb-4">
                  <MessageCircle className="w-12 h-12 text-amber-600 group-hover:scale-110 transition-transform" />
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-amber-600 transition-colors text-center">WhatsApp</h3>
                <p className="text-neutral-400 text-center">+971 56 639 2312</p>
              </a>

              <a
                href="https://www.instagram.com/dj__val/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-instagram-card"
                className="bg-neutral-900 hover:bg-neutral-800 border-2 border-neutral-800 hover:border-amber-600 p-8 transition-all duration-300 rounded-none group"
              >
                <div className="flex justify-center mb-4">
                  <Instagram className="w-12 h-12 text-amber-600 group-hover:scale-110 transition-transform" />
                </div>
                <h3 className="text-xl font-semibold mb-2 group-hover:text-amber-600 transition-colors text-center">Instagram</h3>
                <p className="text-neutral-400 text-center">@dj__val</p>
              </a>
            </div>

            <Button
              data-testid="inquire-now-btn"
              onClick={() => window.location.href = 'mailto:reachdjval@gmail.com'}
              className="bg-amber-600 hover:bg-amber-500 text-black font-semibold px-16 py-8 text-xl rounded-none transition-all duration-300 hover:scale-105 tracking-wider"
            >
              INQUIRE NOW
            </Button>

            {/* Social & Download Links */}
            <div className="mt-16 pt-12 border-t border-neutral-800">
              <p className="text-center text-neutral-400 mb-8 text-lg">Follow &amp; Download</p>
              <div className="flex justify-center items-center gap-8 md:gap-12">
                <a
                  href="https://www.mixcloud.com/DJ__VAL/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-mixcloud-link"
                  className="group flex flex-col items-center gap-3"
                >
                  <div className="w-20 h-20 flex items-center justify-center bg-neutral-900 border-2 border-neutral-800 hover:border-amber-600 transition-all duration-300 group-hover:scale-110">
                    <svg className="w-12 h-12 text-amber-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2.462 8.596l1.372 6.49h.319l1.372-6.49h1.308v7.5h-.913v-5.519l-1.372 5.519h-.768l-1.372-5.519v5.519h-.913v-7.5h1.308zm5.847 0h.913v7.5h-.913v-7.5zm5.263 0l1.372 6.49h.319l1.372-6.49h1.308v7.5h-.913v-5.519l-1.372 5.519h-.768l-1.372-5.519v5.519h-.913v-7.5h1.308zm6.649 0h.913v7.5h-.913v-7.5z"/>
                    </svg>
                  </div>
                  <span className="text-sm text-neutral-400 group-hover:text-amber-600 transition-colors">Mixcloud</span>
                </a>

                <a
                  href="https://youtube.com/@velariodcosta7589?si=AuWG9eRk2psA7IJT"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-youtube-link"
                  className="group flex flex-col items-center gap-3"
                >
                  <div className="w-20 h-20 flex items-center justify-center bg-neutral-900 border-2 border-neutral-800 hover:border-amber-600 transition-all duration-300 group-hover:scale-110">
                    <Youtube className="w-12 h-12 text-amber-600" />
                  </div>
                  <span className="text-sm text-neutral-400 group-hover:text-amber-600 transition-colors">YouTube</span>
                </a>

                <a
                  href="https://drive.google.com/drive/folders/1i4O5TdPx67OklarFt923wFagNH9xThga?usp=share_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-downloads-link"
                  className="group flex flex-col items-center gap-3"
                >
                  <div className="w-20 h-20 flex items-center justify-center bg-neutral-900 border-2 border-neutral-800 hover:border-amber-600 transition-all duration-300 group-hover:scale-110">
                    <svg className="w-12 h-12 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </div>
                  <span className="text-sm text-neutral-400 group-hover:text-amber-600 transition-colors">Downloads</span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer" className="py-12 px-6 bg-black border-t border-neutral-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-neutral-500 text-sm">DJ VAL © 2026. All Rights Reserved</p>
            <div className="flex gap-8">
              <a
                href="https://www.instagram.com/dj__val/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="text-white hover:text-amber-600 transition-colors"
                aria-label="DJ VAL on Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
              <a
                href="http://www.youtube.com/@velariodcosta7589"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube-link"
                className="text-white hover:text-amber-600 transition-colors"
                aria-label="DJ VAL on YouTube"
              >
                <Youtube className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
